import React, { useState, useEffect } from "react";
import DataTable from "react-data-table-component";
import { formatDateTime } from "../../utils/formatters";

/**
 * Componente de tabla reutilizable con búsqueda, paginación y carga remota optimizada.
 *
 * @param {Array} columns - Definición de columnas de la tabla.
 * @param {Array} data - Datos que se mostrarán en la tabla.
 * @param {boolean} loading - Estado de carga (muestra loader).
 * @param {number} totalRows - Total de registros para la paginación.
 * @param {number} perPage - Cantidad de filas por página.
 * @param {number} currentPage - Página actual.
 * @param {Function} onPageChange - Callback al cambiar de página.
 * @param {Function} onRowsPerPageChange - Callback al cambiar el número de filas.
 * @param {Function} onSearch - Callback que recibe el término de búsqueda.
 * @param {string} keyField - Campo clave único por fila (default: "id").
 */

const customStyles = {
  headCells: {
    style: {
      justifyContent: "center",
      textAlign: "center",
      fontWeight: "600",
      color: "inherit",
    },
  },
};

const PaginatedTable = ({
  columns,
  data,
  loading = false,
  totalRows,
  perPage,
  currentPage,
  onPageChange,
  onRowsPerPageChange,
  onSearch,
  keyField = "id",
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [debounceTimer, setDebounceTimer] = useState(null);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    if (debounceTimer) clearTimeout(debounceTimer);

    const timer = setTimeout(() => {
      if (onSearch) {
        const trimmed = value.trim();
        onSearch(trimmed.length >= 3 ? trimmed : ""); // Solo buscar si tiene al menos 3 caracteres
      }
    }, 400);

    setDebounceTimer(timer);
  };

  const clearSearch = () => {
    setSearchTerm("");
    if (onSearch) onSearch("");
  };

  useEffect(() => {
    return () => {
      if (debounceTimer) clearTimeout(debounceTimer);
    };
  }, [debounceTimer]);

  return (
    <>
      <div className="mb-3 position-relative">
        <input
          type="text"
          placeholder="Buscar (mínimo 3 caracteres)..."
          value={searchTerm}
          onChange={handleSearchChange}
          className="form-control pe-5"
        />
        {searchTerm.length > 0 && (
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary position-absolute end-0 top-50 translate-middle-y me-2"
            onClick={clearSearch}
            style={{ zIndex: 2 }}
            title="Limpiar búsqueda"
          >
            &times;
          </button>
        )}
      </div>

      <div className="datatable-wrapper">
        <DataTable
          keyField={keyField}
          columns={columns}
          data={data}
          progressPending={loading}
          pagination
          paginationServer
          paginationTotalRows={totalRows}
          paginationDefaultPage={currentPage}
          paginationPerPage={perPage}
          paginationRowsPerPageOptions={[5, 10, 15, 20]}
          onChangePage={onPageChange}
          onChangeRowsPerPage={onRowsPerPageChange}
          highlightOnHover
          striped
          dense
          noDataComponent="No hay registros disponibles"
          customStyles={customStyles}
          paginationComponentOptions={{
            rowsPerPageText: "Filas por página",
            rangeSeparatorText: "de",
            selectAllRowsItem: false,
          }}
        />
      </div>
    </>
  );
};

export default PaginatedTable;
